import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import { Button, CircularProgress } from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import useReportQueryParams from 'components/ReportsModule/hooks/use-report-query-params';
import { resolveDateRange } from 'components/ReportsModule/utils/report-date-utils';
import { exportReport } from 'components/ReportsModule/utils/report-export-util';
import { ReportWorkbook } from 'components/ReportsModule/types';
import Config from 'config';

interface Props<T> {
  workbook: ReportWorkbook<T>;
}

const ReportExportButtonAsync = <T extends object>({
  workbook
}: Props<T>): React.ReactElement<Props<T>> => {
  const [reportParams] = useReportQueryParams();
  const { t } = useTranslation('Reports');
  const { entityId, dateRange } = reportParams;
  const { dateStart, dateEnd } = resolveDateRange(dateRange);

  const [fetchExportData, { data, error, loading, stopPolling }] = useLazyQuery(workbook.query, {
    errorPolicy: 'all',
    pollInterval: Config.reporting.asyncPollIntervalMs
  });

  const handleExportClick = (): void => {
    fetchExportData({ variables: { id: entityId, dateRange: { dateStart, dateEnd } } });
  };

  if (error) {
    stopPolling?.();
  } else if (data && workbook.isComplete?.(data)) {
    stopPolling?.();
    exportReport(workbook, data, reportParams);
  }

  return (
    <Button
      onClick={handleExportClick}
      variant="outlined"
      color="secondary"
      disableElevation
      startIcon={loading ? <CircularProgress size={14} /> : <GetAppIcon />}
    >
      {t('Export Data')}
    </Button>
  );
};

export default ReportExportButtonAsync;
